import Box from 'components/shared/Box'
import Modal from 'components/shared/Modal'
import Text from 'components/shared/Text'
import {
    ModalHeader,
    ModalFooter,
    ModalFooterListItemsWrapper,
} from 'components/global/CalendarModal/styles'
import { FormattedMessage } from 'react-intl'
import { useEffect, useState } from 'react'
import badgePlus from 'assets/icons/badge_plus.svg'
import badgeWarning from 'assets/icons/badge_warning.svg'
import { useGetCardCalendarQuery } from 'store/api-slice/hooks'
import { useApp } from 'store/steps'
import ModalFooterListItem from './ModalFooterListItem'
import SimpleCalendar from './SimpleCalendar'
import { CalendarLoader } from 'components/shared/Calendar/components/CalendarLoader'

interface CalendarProps {
    isOpened: boolean
    onClose: () => void
}

const UnlimitedCalendarModal = ({ isOpened, onClose }: CalendarProps) => {
    const { language } = useApp()
    const [isCalendarDisabled, setIsCalendarDisabled] = useState(true)
    const { data, isFetching } = useGetCardCalendarQuery(
        {
            _locale: language,
        },
        { skip: !isOpened }
    )

    useEffect(() => {
        if (isFetching || !data) return
        setIsCalendarDisabled(false)
    }, [isFetching])

    return (
        <Modal
            isOpened={isOpened}
            maxWidth={786}
            onClose={onClose}
            isMobileFullscreen={true}
            testIdPrefix="unlimited-calendar"
        >
            <>
                <ModalHeader
                    pt={[24, 32]}
                    pr={[56, 40]}
                    pb={[16, 32]}
                    pl={[16, 40]}
                >
                    <Text variant="body" mb={4} color="grayscale_900">
                        <FormattedMessage
                            id="annual.calendar.title"
                            defaultMessage="Kalendarz wejść <span>z kartą Unlimited/Unlimited+</span>"
                            description="Keep the html tags in the translation"
                            values={{
                                span: (chunks: string[]) => (
                                    <>
                                        <br />
                                        <Text
                                            as="span"
                                            variant="bodyLarge"
                                            color="secondary_500"
                                        >
                                            {chunks}
                                        </Text>
                                    </>
                                ),
                            }}
                        />
                    </Text>
                </ModalHeader>

                {isFetching ? (
                    <CalendarLoader />
                ) : (
                    <>
                        <Box>
                            <SimpleCalendar
                                mode="unlimitedCard"
                                handleDatesChosen={() => null}
                                datesInfo={data || null}
                                isLimitedRange={true}
                                isDisabled={isCalendarDisabled}
                                date={null}
                                testIdPrefix="unlimited"
                            />
                        </Box>
                        <ModalFooter gap={[0, 16]} isFullWidth>
                            <Box
                                align="flex-start"
                                justify="flex-start"
                                direction={['column', 'row']}
                                gap={[0, 24]}
                            >
                                {/* TODO: rewrite using <CalendarLegendItem /> */}
                                <ModalFooterListItemsWrapper
                                    direction="column"
                                    gap={16}
                                    align="flex-start"
                                >
                                    <ModalFooterListItem legendColor="secondary_500">
                                        <FormattedMessage
                                            id="annual.calendar.legend_free_entrance_for_all_cards"
                                            defaultMessage="Wejście dostępne dla każdego typu karty"
                                        />
                                    </ModalFooterListItem>
                                    <ModalFooterListItem
                                        legendColor="primary_500"
                                        badge={badgePlus}
                                    >
                                        <FormattedMessage
                                            id="annual.calendar.legend_free_entrance_for_unlimited_plus"
                                            defaultMessage="Wejście dostępne tylko z kartą Unlimited+ lub za dopłatą (obejmuje weekendy, wakacje, ferie i wybrane dni robocze)"
                                            description="Keep the html tags in the translation"
                                        />
                                    </ModalFooterListItem>
                                    <ModalFooterListItem
                                        legendColor="error"
                                        badge={badgeWarning}
                                    >
                                        <FormattedMessage
                                            id="annual.calendar.legend_park_closed"
                                            defaultMessage="Park nieczynny"
                                        />
                                    </ModalFooterListItem>
                                </ModalFooterListItemsWrapper>
                            </Box>
                        </ModalFooter>
                    </>
                )}
            </>
        </Modal>
    )
}

export default UnlimitedCalendarModal
